// Common types used across the application

export enum UserRole {
  ADMIN = 'admin',
  MANAGER = 'manager',
  AGENT = 'agent',
  FINANCE = 'finance',
  CUSTOMER_SERVICE = 'customer_service',
}

export interface User {
  id: string;
  email: string;
  full_name: string;
  role: UserRole;
  is_active: boolean;
  phone?: string;
  position?: string;
  line_id?: string;
  department?: string;
  avatar_config?: Record<string, any>;
  must_change_password?: boolean;
  last_login_at?: string;
  locked_until?: string;
  password_changed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  user: User;
  must_change_password?: boolean;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  skip: number;
  limit: number;
}

export interface ApiError {
  detail: string;
}

// Insurance Company types
export type CompanyType = 'non_life' | 'life';

export interface DepartmentContact {
  id?: string;
  phone?: string;
  email?: string;
  contact_person?: string;
  remark?: string;
}

export interface CompanyDepartment {
  id?: string;
  company_id?: string;
  department_name: string;
  contacts: DepartmentContact[];
  // Legacy fields (kept for backward compatibility)
  phone?: string;
  email?: string;
  contact_person?: string;
  claims_contact?: string;
  created_at?: string;
  updated_at?: string;
}

export interface DocumentShippingAddress {
  recipient_name: string;
  recipient_phone: string;
  house_number: string;
  moo?: string;
  building?: string;
  floor?: string;
  room?: string;
  village?: string;
  soi?: string;
  road?: string;
  subdistrict: string;
  district: string;
  province: string;
  postal_code: string;
  landmark?: string;
}

export interface InsuranceCompany {
  id: string;
  company_type: CompanyType;
  company_code: string;
  tax_id: string;
  departments?: CompanyDepartment[];
  shipping_address?: DocumentShippingAddress;
  // Legacy fields (kept for backward compatibility)
  name?: string;
  registration_number?: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  // Thai address components
  house_number?: string;
  moo?: string;
  building?: string;
  soi?: string;
  road?: string;
  subdistrict?: string;
  district?: string;
  province?: string;
  postal_code?: string;
  commission_structure?: Record<string, number>;
  notes?: string;
  is_active: boolean;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
}

export interface InsuranceCompanyCreate {
  company_type: CompanyType;
  company_code: string;
  tax_id: string;
  departments?: CompanyDepartment[];
  shipping_address?: DocumentShippingAddress;
  // Legacy fields (optional)
  name?: string;
  registration_number?: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  // Thai address components
  house_number?: string;
  moo?: string;
  building?: string;
  soi?: string;
  road?: string;
  subdistrict?: string;
  district?: string;
  province?: string;
  postal_code?: string;
  commission_structure?: Record<string, number>;
  notes?: string;
}

export interface InsuranceCompanyUpdate {
  company_type?: CompanyType;
  company_code?: string;
  tax_id?: string;
  shipping_address?: DocumentShippingAddress;
  // Legacy fields
  name?: string;
  registration_number?: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  // Thai address components
  house_number?: string;
  moo?: string;
  building?: string;
  soi?: string;
  road?: string;
  subdistrict?: string;
  district?: string;
  province?: string;
  postal_code?: string;
  commission_structure?: Record<string, number>;
  departments?: CompanyDepartment[];
  notes?: string;
  is_active?: boolean;
}

// Agent types
export type LicenseType = 'life_agent' | 'non_life_agent' | 'life_broker' | 'non_life_broker';

export interface AgentLicense {
  license_number: string;
  license_type: LicenseType | '';
  expiry_date: string;
}

export interface AgentAddress {
  house_number: string;
  moo?: string;
  building?: string;
  soi?: string;
  road?: string;
  subdistrict: string;
  subdistrict_code: number;
  district: string;
  district_code: number;
  province: string;
  province_code: number;
  postal_code: string;
}

export interface AgentDocument {
  id: string;
  agent_id: string;
  filename: string;
  original_filename: string;
  file_type: string;
  file_size: number;
  upload_date: string;
  uploaded_by: string;
}

export interface Agent {
  id: string;
  agent_id: string;
  full_name: string;
  sex?: string; // Gender: 'male' or 'female'
  // Primary license fields
  primary_license_number: string;
  primary_license_type?: LicenseType;
  primary_license_expiry_date?: string;
  primary_license_expiry_alert?: string;
  // Secondary license fields
  secondary_license_number?: string;
  secondary_license_type?: LicenseType;
  secondary_license_expiry_date?: string;
  secondary_license_expiry_alert?: string;
  // New multiple licenses support
  licenses?: AgentLicense[];
  phone?: string;
  email?: string;
  line_id?: string;
  address?: string; // Legacy field
  structured_address?: AgentAddress;
  bank_name?: string;
  bank_account_number?: string;
  bank_account_name?: string;
  team_id?: string;
  team_name?: string; // Team name from backend (Requirement 7.2)
  hire_date?: string;
  termination_date?: string;
  is_active: boolean;
  is_deleted: boolean;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface AgentCreate {
  agent_id: string;
  full_name: string;
  sex: string; // Required: 'male' or 'female'
  // Primary license fields
  primary_license_number: string;
  primary_license_type?: LicenseType;
  primary_license_expiry_date?: string;
  // Secondary license fields
  secondary_license_number?: string;
  secondary_license_type?: LicenseType;
  secondary_license_expiry_date?: string;
  // New multiple licenses support
  licenses?: AgentLicense[];
  phone?: string;
  email?: string;
  line_id?: string;
  address?: string; // Legacy field
  structured_address?: AgentAddress;
  bank_name?: string;
  bank_account_number?: string;
  bank_account_name?: string;
  team_id?: string;
  hire_date?: string;
  is_active?: boolean;
  notes?: string;
}

export interface AgentUpdate {
  agent_id?: string;
  full_name?: string;
  sex?: string; // 'male' or 'female'
  // Primary license fields
  primary_license_number?: string;
  primary_license_type?: LicenseType;
  primary_license_expiry_date?: string;
  // Secondary license fields
  secondary_license_number?: string;
  secondary_license_type?: LicenseType;
  secondary_license_expiry_date?: string;
  // New multiple licenses support
  licenses?: AgentLicense[];
  phone?: string;
  email?: string;
  line_id?: string;
  address?: string; // Legacy field
  structured_address?: AgentAddress;
  bank_name?: string;
  bank_account_number?: string;
  bank_account_name?: string;
  team_id?: string;
  hire_date?: string;
  termination_date?: string;
  is_active?: boolean;
  notes?: string;
}

export interface AgentPerformanceMetrics {
  total_policies: number;
  active_policies: number;
  total_premium: string;
  total_commission: string;
  pending_commission: string;
  paid_commission: string;
}

export interface AgentDetail extends Agent {
  performance?: AgentPerformanceMetrics;
  quotations?: QuotationSummary[];
  policies?: PolicySummary[];
}

// Quotation and Policy Summary types for agent/customer/team views
export interface QuotationSummary {
  id: string;
  quote_number: string;
  quote_date: string;
  total_premium: number | string;  // Can be number or string depending on source
  status: string;
  customer_name?: string;  // For agent/team view
  agent_name?: string;  // For customer/team view
  agent_id?: string;  // For navigation
}

export interface PolicySummary {
  id: string;
  policy_number: string;
  product_name?: string;  // Product name for display
  policy_type?: string;  // Type of policy (life, non_life)
  insurance_type?: string;  // Specific insurance type (car, health, etc.)
  insurance_company_name?: string;  // Insurance company name
  start_date: string;
  end_date: string;
  status: string;
  sum_insured?: number;  // Sum insured amount
  premium: number | string;  // Can be number or string depending on source
  customer_name?: string;  // For agent/team view
  agent_name?: string;  // For customer/team view
}

// Team types
export interface Team {
  id: string;
  name: string;
  team_leader_id?: string;
  team_leader_name?: string;
  description?: string;
  commission_split?: Record<string, number>;
  is_active: boolean;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
  member_count?: number;  // Optional for list view
}

export interface TeamCreate {
  name: string;
  team_leader_id?: string;
  description?: string;
  commission_split?: Record<string, number>;
  is_active?: boolean;
}

export interface TeamUpdate {
  name?: string;
  team_leader_id?: string;
  description?: string;
  commission_split?: Record<string, number>;
  is_active?: boolean;
}

export interface TeamPerformanceMetrics {
  team_id: string;
  team_name: string;
  total_members: number;
  active_members: number;
  total_policies: number;
  active_policies: number;
  total_premium: string;
  total_commission: string;
  pending_commission: string;
  paid_commission: string;
}

export interface TeamDetail extends Team {
  member_count: number;
  performance?: TeamPerformanceMetrics;
  quotations?: QuotationSummary[];
  policies?: PolicySummary[];
}

// Customer types
export enum CustomerType {
  INDIVIDUAL = 'individual',
  CORPORATE = 'corporate',
  UNSPECIFIED = 'unspecified',
}

export interface Customer {
  id: string;
  customer_type: CustomerType;
  full_name: string;
  sex?: string; // Gender (for individual customers): 'male' or 'female'
  id_card_number?: string;
  date_of_birth?: string;
  company_name?: string;
  tax_id?: string;
  contact_person?: string;
  phone?: string;
  email?: string;
  line_id?: string;
  address?: string;
  // Structured Thai address fields
  house_number?: string;
  moo?: string;
  building?: string;
  soi?: string;
  road?: string;
  subdistrict?: string;
  district?: string;
  province?: string;
  postal_code?: string;
  marketing_consent: boolean;
  notes?: string;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
  // Enhanced list data fields
  total_policies?: number;
  active_policies?: number;
  contact_count?: number;
  primary_contact_name?: string;
  expiry_status?: 'expired' | 'expiring_soon' | 'active' | null;
  days_until_expiry?: number | null;
}

export interface CustomerCreate {
  customer_type: CustomerType;
  full_name: string;
  sex?: string; // Required for individual, not for corporate
  id_card_number?: string;
  date_of_birth?: string;
  company_name?: string;
  tax_id?: string;
  contact_person?: string; // Contact person for corporate customers
  phone?: string;
  email?: string;
  line_id?: string;
  address?: string;
  marketing_consent?: boolean;
  notes?: string;
}

export interface CustomerUpdate {
  customer_type?: CustomerType;
  full_name?: string;
  sex?: string; // 'male' or 'female'
  id_card_number?: string;
  date_of_birth?: string;
  company_name?: string;
  tax_id?: string;
  phone?: string;
  email?: string;
  line_id?: string;
  address?: string;
  marketing_consent?: boolean;
  notes?: string;
}

// Contact and Address types for corporate customers
export interface ContactAddress {
  id?: string;
  contact_id?: string;
  house_number?: string;
  moo?: string;
  building?: string;
  soi?: string;
  road?: string;
  subdistrict?: string;
  district?: string;
  province?: string;
  postal_code?: string;
}

export interface CorporateContact {
  id?: string;
  customer_id?: string;
  department: string;
  email?: string;
  phone?: string;
  coordinator_name?: string;
  addresses: ContactAddress[];
}

// Policy types for customer insurance management
export enum PolicyType {
  LIFE = 'life',
  NON_LIFE = 'non_life',
}

export enum PolicyInsuranceCategory {
  MOTOR = 'motor',
  FIRE_AND_RISK = 'fire_and_risk',
  MISCELLANEOUS = 'miscellaneous',
  MARINE = 'marine',
  GROUP = 'group',
  COMPULSORY = 'compulsory',
}

export interface PolicyRider {
  rider_name: string;
  rider_details?: string;
}

export interface PolicyAttachment {
  id: string;
  policy_id: string;
  filename: string;
  file_path: string;
  file_size: number;
  content_type: string;
  uploaded_at: string;
}

export interface CustomerPolicy {
  id?: string;
  policy_type: PolicyType;
  policy_number: string;

  // Non-life insurance fields
  insurance_company_id?: string;
  insurance_category?: PolicyInsuranceCategory;
  has_compulsory?: boolean;
  compulsory_policy_number?: string;

  // Product reference (optional)
  product_id?: string;
  product_name?: string; // For display only

  // Category-specific fields for non-life insurance
  license_plate?: string; // ทะเบียนรถ (Motor, Marine)
  insured_property_address?: string; // เลขที่บ้าน/อาคารที่เอาประกัน (Fire and Risk)
  category_specific_info?: string; // ข้อมูลเบื้องต้น (Miscellaneous, Group)

  // Coverage dates (for motor insurance)
  // Default: true (พรบ uses same dates as ภาคสมัครใจ)
  // If false: show separate date pickers for พรบ
  same_coverage_dates?: boolean;
  compulsory_start_date?: string;  // Only if same_coverage_dates = false
  compulsory_end_date?: string;    // Only if same_coverage_dates = false

  // Life insurance fields
  main_contract_name?: string;
  main_contract_details?: string;
  riders?: PolicyRider[];

  // Common fields
  start_date: string;
  end_date: string;
  policy_details?: string;
  endorsement?: string;

  // File attachments
  attachments?: PolicyAttachment[];
}

// NOTE: PolicySummary and QuotationSummary are defined earlier in this file (lines 314-336)
// This section previously had duplicate definitions that have been removed

export interface CustomerDetail extends Customer {
  policies: PolicySummary[];
  quotations: QuotationSummary[];  // Requirement 4.2
  total_policies: number;
  active_policies: number;
  total_premium: number;
}

// Product types
export enum InsuranceType {
  CAR = 'car',
  HEALTH = 'health',
  LIFE = 'life',
  FIRE = 'fire',
  TRAVEL = 'travel',
  ACCIDENT = 'accident',
  OTHER = 'other',
}

// NEW: Enhanced product management enums
export enum InsuranceCategory {
  NON_LIFE = 'non_life',  // ประกันภัย
  LIFE = 'life',          // ประกันชีวิต
}

export enum NonLifeInsuranceType {
  MOTOR = 'motor',                    // ประกันรถยนต์
  FIRE_AND_RISK = 'fire_and_risk',    // ประกันอัคคีภัยและประกันความเสี่ยงภัย
  MISCELLANEOUS = 'miscellaneous',    // ประกันภัยเบ็ดเตล็ด
  MARINE = 'marine',                  // ประกันการขนส่ง
  GROUP = 'group',                    // ประกันกลุ่ม
  COMPULSORY = 'compulsory',          // พรบ (ประกันภัยรถยนต์ภาคบังคับ)
}

export enum LifeInsuranceType {
  ORDINARY = 'ordinary',              // ประกันสามัญ
  INDUSTRIAL = 'industrial',          // ประกันอุตสาหกรรม (OIC นปช.1)
  ACCIDENT = 'accident',              // ประกันอุบัติเหตุส่วนบุคคล (PA)
  GROUP = 'group',                    // ประกันกลุ่ม
  RIDER = 'rider',                    // สัญญาเพิ่ม
}

export enum ProductStatus {
  ACTIVE = 'active',
  CANCEL = 'cancel',
  OTHER = 'other',
}

export enum CommissionType {
  ABSOLUTE = 'absolute',    // Fixed amount in Baht
  PERCENTAGE = 'percentage', // Percentage of premium
}

// Thai labels for insurance types
export const NonLifeInsuranceTypeLabels: Record<NonLifeInsuranceType, string> = {
  [NonLifeInsuranceType.MOTOR]: 'ประกันรถยนต์',
  [NonLifeInsuranceType.FIRE_AND_RISK]: 'ประกันอัคคีภัยและประกันความเสี่ยงภัย',
  [NonLifeInsuranceType.MISCELLANEOUS]: 'ประกันภัยเบ็ดเตล็ด',
  [NonLifeInsuranceType.MARINE]: 'ประกันการขนส่ง',
  [NonLifeInsuranceType.GROUP]: 'ประกันกลุ่ม',
  [NonLifeInsuranceType.COMPULSORY]: 'พรบ (ประกันภัยรถยนต์ภาคบังคับ)',
};

export const LifeInsuranceTypeLabels: Record<LifeInsuranceType, string> = {
  [LifeInsuranceType.ORDINARY]: 'ประกันสามัญ',
  [LifeInsuranceType.INDUSTRIAL]: 'ประกันอุตสาหกรรม',
  [LifeInsuranceType.ACCIDENT]: 'ประกันอุบัติเหตุส่วนบุคคล (PA)',
  [LifeInsuranceType.GROUP]: 'ประกันกลุ่ม',
  [LifeInsuranceType.RIDER]: 'สัญญาเพิ่ม',
};

export interface ProductAttachment {
  id: string;
  product_id: string;
  filename: string;
  file_path: string;
  file_size: number;
  content_type: string;
  uploaded_at: string;
  created_at: string;
  updated_at: string;
  is_deleted: boolean;
}

export interface InsuranceProduct {
  id: string;
  product_code: string;
  name: string;
  company_id: string;
  company_name?: string;

  // NEW: Hierarchical categorization
  insurance_category?: InsuranceCategory;
  insurance_type_new?: string;  // Stores value from NonLifeInsuranceType or LifeInsuranceType

  // NEW: Product details
  status?: ProductStatus;
  commission_type?: CommissionType;
  commission?: number;
  ov?: number;
  remark?: string;

  // Legacy fields (kept for backward compatibility)
  insurance_type?: InsuranceType;
  base_premium?: number;
  commission_rate?: number;
  coverage_details?: Record<string, any>;
  terms_and_conditions?: string;

  description?: string;
  is_active: boolean;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;

  // Attachments
  attachments?: ProductAttachment[];
}

export interface InsuranceProductCreateEnhanced {
  // Hierarchical selection
  insurance_category: InsuranceCategory;
  company_id: string;
  insurance_type: string;  // Value from NonLifeInsuranceType or LifeInsuranceType

  // Product details
  name: string;
  status: ProductStatus;
  is_active?: boolean;
  description?: string;
  commission_type?: CommissionType;
  commission?: number;
  ov?: number;
  remark?: string;

  // Auto-generated
  product_code?: string;
}

export interface InsuranceProductUpdateEnhanced {
  // Hierarchical selection
  insurance_category?: InsuranceCategory;
  company_id?: string;
  insurance_type?: string;

  // Product details
  name?: string;
  status?: ProductStatus;
  description?: string;
  commission_type?: CommissionType;
  commission?: number;
  ov?: number;
  remark?: string;
  product_code?: string;
  is_active?: boolean;
}

export interface InsuranceProductCreate {
  product_code: string;
  name: string;
  insurance_type: InsuranceType;
  company_id: string;
  description?: string;
  coverage_details?: Record<string, any>;
  base_premium?: number;
  commission_rate?: number;
  is_active?: boolean;
}

export interface InsuranceProductUpdate {
  product_code?: string;
  name?: string;
  insurance_type?: InsuranceType;
  company_id?: string;
  description?: string;
  coverage_details?: Record<string, any>;
  base_premium?: number;
  commission_rate?: number;
  is_active?: boolean;
}

// Commission types
export enum CommissionStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  PAID = 'PAID',
  CANCELLED = 'CANCELLED',
}

export interface Commission {
  id: string;
  policy_id: string;
  agent_id: string;
  premium_amount: number;
  commission_rate: number;
  commission_amount: number;
  company_share_rate: number;
  company_commission: number;
  agent_commission: number;
  status: CommissionStatus;
  approved_by?: string;
  approved_at?: string;
  paid_by?: string;
  paid_at?: string;
  payment_reference?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface CommissionDetail extends Commission {
  policy_number?: string;
  agent_name?: string;
  customer_name?: string;
  approved_by_name?: string;
  paid_by_name?: string;
}

export interface CommissionApprove {
  notes?: string;
}

export interface CommissionPay {
  payment_reference: string;
  notes?: string;
}

// Transaction types
export enum TransactionType {
  RECEIPT = 'receipt',
  PAYMENT = 'payment',
}

export enum PaymentMethod {
  CASH = 'cash',
  BANK_TRANSFER = 'bank_transfer',
  CHEQUE = 'cheque',
  CREDIT_CARD = 'credit_card',
  PROMPTPAY = 'promptpay',
  OTHER = 'other',
}

export interface Transaction {
  id: string;
  transaction_number: string;
  transaction_type: TransactionType;
  transaction_date: string;
  amount: number;
  payment_method: PaymentMethod;
  customer_id?: string;
  agent_id?: string;
  policy_id?: string;
  reference_number?: string;
  description?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface TransactionDetail extends Transaction {
  customer_name?: string;
  agent_name?: string;
  policy_number?: string;
}

export interface TransactionCreate {
  transaction_type: TransactionType;
  transaction_date: string;
  amount: number;
  payment_method: PaymentMethod;
  customer_id?: string;
  agent_id?: string;
  policy_id?: string;
  reference_number?: string;
  description?: string;
  notes?: string;
}

export interface TransactionUpdate {
  transaction_date?: string;
  amount?: number;
  payment_method?: PaymentMethod;
  reference_number?: string;
  description?: string;
  notes?: string;
}

// Receivables types
export interface ReceivableItem {
  customer_id: string;
  customer_name: string;
  policy_id?: string;
  policy_number?: string;
  total_amount: number;
  paid_amount: number;
  outstanding_amount: number;
  current: number;
  days_30: number;
  days_60: number;
  days_90_plus: number;
  oldest_invoice_date?: string;
}

export interface ReceivablesResponse {
  items: ReceivableItem[];
  total_outstanding: number;
  total_current: number;
  total_30_days: number;
  total_60_days: number;
  total_90_plus_days: number;
}

// Payables types
export interface PayableItem {
  agent_id: string;
  agent_name: string;
  total_pending: number;
  total_approved: number;
  total_unpaid: number;
  commission_count: number;
  oldest_commission_date?: string;
}

export interface PayablesResponse {
  items: PayableItem[];
  total_pending: number;
  total_approved: number;
  total_unpaid: number;
}

// Cash Flow types
export interface CashFlowSummary {
  period_start: string;
  period_end: string;
  total_receipts: number;
  receipts_by_method: Record<string, number>;
  total_payments: number;
  payments_by_method: Record<string, number>;
  net_cash_flow: number;
  receipt_count: number;
  payment_count: number;
}

// Customer Debt types
export interface CustomerDebtResponse {
  customer_id: string;
  customer_name: string;
  total_invoiced: number;
  total_paid: number;
  total_outstanding: number;
  overdue_amount: number;
  days_overdue?: number;
  policies: Array<{
    policy_id?: string;
    policy_number?: string;
    invoice_date: string;
    due_date?: string;
    amount: number;
    is_overdue: boolean;
  }>;
}

// Agent Debt types
export interface AgentDebtResponse {
  agent_id: string;
  agent_name: string;
  total_earned: number;
  total_paid: number;
  total_outstanding: number;
  pending_count: number;
  approved_count: number;
  oldest_pending_date?: string;
}

// Service Ticket types
export enum TicketType {
  CLAIM = 'claim',
  INQUIRY = 'inquiry',
  COMPLAINT = 'complaint',
  RENEWAL = 'renewal',
  CHANGE_REQUEST = 'change_request',
  OTHER = 'other',
}

export enum TicketPriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  URGENT = 'urgent',
}

export enum TicketStatus {
  OPEN = 'open',
  IN_PROGRESS = 'in_progress',
  RESOLVED = 'resolved',
  CLOSED = 'closed',
}

export interface ServiceTicket {
  id: string;
  ticket_number: string;
  ticket_type: TicketType;
  priority: TicketPriority;
  status: TicketStatus;
  customer_id: string;
  policy_id?: string;
  assigned_to?: string;
  subject: string;
  description: string;
  resolution_notes?: string;
  resolved_at?: string;
  resolved_by?: string;
  created_at: string;
  updated_at: string;
}

export interface ServiceTicketDetail extends ServiceTicket {
  customer_name?: string;
  policy_number?: string;
  assigned_to_name?: string;
  resolved_by_name?: string;
}

export interface ServiceTicketCreate {
  ticket_type: TicketType;
  priority?: TicketPriority;
  customer_id: string;
  policy_id?: string;
  subject: string;
  description: string;
}

export interface ServiceTicketUpdate {
  ticket_type?: TicketType;
  priority?: TicketPriority;
  status?: TicketStatus;
  subject?: string;
  description?: string;
}

export interface ServiceTicketAssign {
  assigned_to: string;
}

export interface ServiceTicketResolve {
  resolution_notes: string;
}

export interface TicketStatistics {
  total_tickets: number;
  open_tickets: number;
  in_progress_tickets: number;
  resolved_tickets: number;
  closed_tickets: number;
  by_type: Record<string, number>;
  by_priority: Record<string, number>;
  avg_resolution_time_hours?: number;
}

// Renewal Reminder types
export interface RenewalReminder {
  id: string;
  policy_id: string;
  customer_id: string;
  reminder_date: string;
  days_before_expiry: number;
  is_sent: boolean;
  sent_at?: string;
  email_sent_to?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface UpcomingRenewal {
  policy_id: string;
  policy_number: string;
  customer_id: string;
  customer_name: string;
  customer_email?: string;
  customer_phone?: string;
  product_name: string;
  insurance_type: string;
  premium: number;
  end_date: string;
  days_until_expiry: number;
  has_reminder: boolean;
  reminder_sent: boolean;
}

export interface RenewalReminderSendRequest {
  policy_ids: string[];
}

export interface RenewalReminderSendResponse {
  total_requested: number;
  sent_successfully: number;
  failed: number;
  errors: string[];
}

// Quotation types
export enum QuotationStatus {
  DRAFT = 'draft',
  SENT = 'sent',
  ACCEPTED = 'accepted',
  REJECTED = 'rejected',
  EXPIRED = 'expired',
  CONVERTED = 'converted',
}

export interface QuotationProduct {
  product_id: string;
  product_name?: string;
  sum_insured: number;
  premium: number;
  coverage_details?: Record<string, any>;
}

export interface Quotation {
  id: string;
  quote_number: string;
  customer_id: string;
  agent_id: string;
  products: QuotationProduct[];
  total_premium: number;
  status: QuotationStatus;
  quote_date: string;  // Added
  expiry_date: string;  // Added (replaces valid_until)
  valid_until: string;  // Keep for backward compatibility
  notes?: string;
  pdf_path?: string;
  created_at: string;
  updated_at: string;
  // Enhanced fields
  customer_type?: 'individual' | 'corporate' | 'unspecified';
  insurance_type?: 'life' | 'non_life';
  details?: string;
  customer_response?: string | null;
  customer_response_date?: string;
  remarks?: string;
  job_submission_id?: string;
  // Relationships for list view
  agent?: {
    id: string;
    full_name: string;
  };
  customer?: {
    id: string;
    full_name?: string;
    company_name?: string;
    phone?: string;
    email?: string;
    address?: string;
  };
  job_submission?: {
    id: string;
    job_number: string;
    job_date: string;
    payment_details?: {
      policy_premiums: Array<{
        policy_type: string;
        amount: number;
      }>;
      total_premium: number;
      trade_discount: number;
      trade_discount_remark?: string;
      commission_discount: number;
      commission_discount_remark?: string;
      withholding_tax: number;
      withholding_tax_remark?: string;
      other_discount: number;
      other_discount_remark?: string;
      net_premium: number;
      payment_status: string;
      payment_method?: string;
      payment_method_other?: string;
      payment_amount?: number;
    };
  };
}

export interface QuotationDetail extends Quotation {
  customer_name?: string;
  customer_email?: string;
  customer_phone?: string;
  agent_name?: string;
  converted_policy_id?: string;
  converted_policy_number?: string;
  quote_date: string;
  expiry_date: string;
}

export interface QuotationCreate {
  customer_id: string;
  agent_id: string;
  products: QuotationProduct[];
  valid_until: string;
  notes?: string;
  team_id_snapshot?: string; // Team assignment at quotation creation (Requirement 14.1)
}

export interface QuotationUpdate {
  products?: QuotationProduct[];
  expiry_date?: string;
  status?: QuotationStatus;
  details?: string;
  notes?: string;
}

export interface QuotationConvertRequest {
  start_date: string;
  end_date: string;
  li_product_type?: string;       // ordinary/industrial/group/pa (LI)
  insurance_category?: string;    // motor/fire_and_risk/marine/compulsory/miscellaneous (GI)
  commission_rate?: number;       // 0–1 decimal
}

export interface QuotationConvertResponse {
  policy_id: string;
  policy_number: string;
  invoice_id: string;
  invoice_number: string;
}

// Enhanced Quotation Types for Sales Workflow
export enum CustomerResponseType {
  BUY = 'buy',
  REJECT = 'reject',
  MODIFY = 'modify',
  CANCEL = 'cancel',
}

export enum PaymentStatus {
  PAID = 'paid',
  BILLED = 'billed',
  PENDING = 'pending',
}

export enum PaymentMethodType {
  CREDIT_CARD = 'credit_card',
  BANK_TRANSFER = 'bank_transfer',
  OTHER = 'other',
}

export interface QuotationAttachment {
  id: string;
  quotation_id: string;
  attachment_type: 'request' | 'proposal';
  filename: string;
  original_filename: string;
  file_path: string;
  file_size: number;
  content_type: string;
  uploaded_at: string;
}

export interface PolicyPremium {
  policy_type: string;
  amount: number;
}

export interface PaymentDetails {
  policy_premiums: PolicyPremium[];
  total_premium: number;
  trade_discount: number;
  trade_discount_remark?: string;
  commission_discount: number;
  commission_discount_remark?: string;
  withholding_tax: number;
  withholding_tax_remark?: string;
  other_discount: number;
  other_discount_remark?: string;
  net_premium: number;
  payment_status: PaymentStatus;
  payment_method?: PaymentMethodType;
  payment_method_other?: string;
  payment_amount?: number;
}

export interface JobSubmission {
  id: string;
  quotation_id: string;
  job_number: string;
  job_date: string;
  customer_id: string;
  payment_details: PaymentDetails;
  status: string;
  submitted_by: string;
  submitted_at: string;
}

export interface EnhancedQuotation extends Quotation {
  customer_type: 'individual' | 'corporate' | 'unspecified';
  insurance_type: 'life' | 'non_life';
  details?: string;
  customer_response?: CustomerResponseType;
  customer_response_date?: string;
  remarks?: string;
  attachments?: QuotationAttachment[];
  job_submission?: JobSubmission;
}

export interface EnhancedQuotationCreate {
  quotation_date: string;
  agent_id: string;
  customer_type: 'individual' | 'corporate' | 'unspecified';
  customer_id?: string;
  insurance_type: 'life' | 'non_life';
  details?: string;
  request_documents?: File[];
  proposal_documents?: File[];
  remarks?: string;
}

export interface CustomerResponseUpdate {
  customer_response: CustomerResponseType;
  customer_response_date: string;
}

export interface JobSubmissionCreate {
  job_date: string;
  customer_data: CustomerCreate | CustomerUpdate;
  payment_details: PaymentDetails;
}

// Invoice types
export interface Invoice {
  id: string;
  invoice_number: string;
  quotation_id?: string;
  customer_id: string;
  policy_id?: string;
  invoice_date: string;
  due_date?: string;
  subtotal: number;
  tax_rate: number;
  tax_amount: number;
  total_amount: number;
  is_paid: boolean;
  paid_at?: string;
  payment_method?: PaymentMethod;
  notes?: string;
  pdf_path?: string;
  created_at: string;
  updated_at: string;
}

export interface InvoiceDetail extends Invoice {
  customer_name?: string;
  customer_tax_id?: string;
  customer_address?: string;
  policy_number?: string;
  quote_number?: string;
}

export interface InvoiceCreate {
  quotation_id?: string;
  customer_id: string;
  policy_id?: string;
  invoice_date: string;
  due_date?: string;
  subtotal: number;
  tax_rate?: number;
  notes?: string;
}

export interface InvoiceUpdate {
  due_date?: string;
  is_paid?: boolean;
  payment_method?: PaymentMethod;
  notes?: string;
}

// Receipt types
export interface Receipt {
  id: string;
  receipt_number: string;
  invoice_id: string;
  transaction_id?: string;
  customer_id: string;
  receipt_date: string;
  amount: number;
  payment_method: PaymentMethod;
  reference_number?: string;
  notes?: string;
  pdf_path?: string;
  created_at: string;
  updated_at: string;
}

export interface ReceiptDetail extends Receipt {
  customer_name?: string;
  invoice_number?: string;
  policy_number?: string;
}

export interface ReceiptCreate {
  invoice_id: string;
  customer_id: string;
  receipt_date: string;
  amount: number;
  payment_method: PaymentMethod;
  reference_number?: string;
  notes?: string;
}

export interface ReceiptCopyRequest {
  copy_number: number;
}

// ============================================================================
// Quotation Three-Stage Workflow Types
// ============================================================================

export * from './quotation';

// ============================================================================
// Policy Management Types
// ============================================================================

export * from './policy';
